import { dayIsUnclosed } from './unclosedDay.js';

/* ===========================================================================
 * WHAT A DAY WAS, SAID IN THREE FACTS INSTEAD OF ONE WORD.
 *
 * `DayStatus` stores ONE value and it is answering THREE questions at once:
 *
 *     was work expected?      WEEKEND  HOLIDAY  ON_LEAVE
 *     did they turn up?       PRESENT  ABSENT
 *     how did the day go?     LATE  HALF_DAY  EARLY_LEAVE  INCOMPLETE
 *
 * Because it is one field, the third question OVERWRITES the second. A worker
 * who arrives late stops being PRESENT. A worker who arrives late and leaves at
 * lunch stops being LATE as well, because HALF_DAY outranks it. That is not a
 * counting bug that can be patched in a tile — it is why ten services grew ten
 * different definitions of "present", and why a summary can read
 * "Present 4 · Late 2 · Total 4" and be internally consistent while looking
 * broken to everyone who reads it.
 *
 * So the three questions are answered separately, and `attendance` is never
 * destroyed by `quality`. A late day is an ATTENDED day. A short day is an
 * ATTENDED day. Present counts stop disagreeing because nothing can remove a
 * person from them except genuinely not turning up.
 *
 * ── WHO IS JUDGED HOW ───────────────────────────────────────────────────────
 * The owner's rule, 2026-09-16, and the reason this file takes TWO inputs
 * rather than one:
 *
 *   LB — the WORKER  (`SITE_WORKER`)   9h on site = 8h work + 1h break.
 *                                      Paid for hours, so hours are the test.
 *   ST — the STAFF   (`OFFICE_STAFF`)  09:00-17:00 sharp in the office with an
 *                                      hour off; on a construction site the
 *                                      same person is flexible.
 *
 * FLEXIBILITY FOLLOWS THE PLACE, NOT THE PERSON. That is the half a person-only
 * model gets wrong: a staff member spending Tuesday on a site is not late at
 * 09:20 that day, and the only thing that knows it is the site they stood on.
 *
 * And for staff on a site the unit of account is the DAY, not the hour —
 * "we don't count their attendance by hours, more about days, so being late
 * means nothing except reporting status". Hence `basis: 'DAY'`, which makes no
 * quality judgement at all while still carrying the minutes for a report.
 *
 * ── LATENESS IS AN ANNOTATION, ALWAYS ───────────────────────────────────────
 * `lateMinutes` and `earlyLeaveMinutes` are returned on every day whatever the
 * basis, and they never move `attendance`. Under `CLOCK` they are promoted to
 * the headline `quality`; under `HOURS` and `DAY` they are reporting only. One
 * recorded fact, two readings — which is the opposite of today, where the fact
 * is discarded by the label.
 * ======================================================================== */

/**
 * Worksite types on which the clock is not read.
 *
 * A construction site and a labour camp are where the flexible population
 * works. Everything else — office, retail, warehouse — is a place with a door
 * that opens at a stated time.
 */
export const FLEXIBLE_WORKSITE_TYPES = ['CONSTRUCTION', 'CAMP'] as const;

/** What this day is judged on. */
export type TimekeepingBasis =
  /** Hours worked against a target. The worker's contract. */
  | 'HOURS'
  /** Arrival and departure times. Office staff in the office. */
  | 'CLOCK'
  /** Turned up or did not. Office staff on a site — days, not hours. */
  | 'DAY';

/** Was work expected today? A fact about the calendar, not the person. */
export type DueKind = 'WORKING_DAY' | 'REST_DAY' | 'HOLIDAY' | 'LEAVE';

/**
 * Did they turn up? THE ONLY FIELD A PRESENT COUNT MAY READ.
 *
 * `UNRESOLVED` is the day that was punched into and never out of. It is
 * deliberately neither ATTENDED nor ABSENT: the rollup force-closes it at local
 * midnight, so its minutes are manufactured rather than measured, and payroll
 * already refuses to pay it (`utils/payableDay.ts`). Calling it ABSENT would
 * accuse somebody who was demonstrably at work; calling it ATTENDED would bill
 * a figure nobody recorded. It is a third answer because it is a third thing.
 */
export type AttendanceMark = 'ATTENDED' | 'ABSENT' | 'UNRESOLVED';

/** The headline judgement, in the vocabulary of whichever basis applies. */
export type DayQuality =
  // HOURS
  | 'FULL_DAY'
  | 'SHORT'
  | 'OVER'
  // CLOCK
  | 'ON_TIME'
  | 'LATE'
  | 'LEFT_EARLY';

export interface DayLabels {
  due: DueKind;
  attendance: AttendanceMark;
  basis: TimekeepingBasis;
  /** `null` when the basis makes no judgement — see `basis: 'DAY'`. */
  quality: DayQuality | null;
  /** Recorded on every basis; only `CLOCK` promotes them to `quality`. */
  lateMinutes: number;
  earlyLeaveMinutes: number;
}

/* ===========================================================================
 * THE TWO HALVES A SUMMARY HAS TO COUNT SEPARATELY.
 *
 * A report gives most statuses a day-count bucket of their own. Four it cannot:
 * the rollup assigns WEEKEND and HOLIDAY from the CALENDAR before it looks at
 * any punch, so a Friday somebody actually worked keeps the calendar's label
 * all day; INCOMPLETE and EARLY_LEAVE are stamped for reasons that say nothing
 * about whether work happened. Their minutes were always summed, but the DAY
 * fell through the bucket switch into no counter at all — which understated
 * `presentDays` on every worked weekend, and with it the attendance rate
 * computed from it.
 *
 * So they are credited by a second, targeted query with `totalWorkedMinutes > 0`
 * in SQL. Both lists live here because THREE report services were each keeping
 * their own copy, one of them with a comment explaining that it was duplicated
 * "to avoid a service-to-service import for four strings" — which is the whole
 * cost of a copy stated out loud and then paid anyway.
 *
 * `attendanceLabels.test.ts` asserts the two are a PARTITION of the enum: every
 * stored status is in exactly one, so a tenth status cannot be added without
 * some report failing to count it.
 * ======================================================================== */

/** Statuses an attendance summary gives a day-count bucket of their own. */
export const BUCKETED_DAY_STATUSES = [
  'PRESENT',
  'LATE',
  'HALF_DAY',
  'ABSENT',
  'ON_LEAVE',
] as const;

/** The complement — credited only when the day carries worked minutes. */
export const OFF_CALENDAR_WORKED_STATUSES = [
  'WEEKEND',
  'HOLIDAY',
  'INCOMPLETE',
  'EARLY_LEAVE',
] as const;

/**
 * Which rule judges this day.
 *
 * `worksiteType` is the site the day was MOSTLY spent on — the same
 * most-minutes-wins choice the ERP export already makes (`dayOwner` in
 * `erpClockcardsExport`), so a day's label and a day's pay can never be decided
 * by two different sites. `null` means no site was resolved, which is the
 * ordinary state for remote staff and for a day with no punches.
 */
export function timekeepingBasis(input: {
  workforceType?: string | null;
  worksiteType?: string | null;
}): TimekeepingBasis {
  // The worker is paid for hours wherever they stand, so the place does not
  // change the test for them — only for staff.
  if (input.workforceType === 'SITE_WORKER') return 'HOURS';
  const flexible =
    input.worksiteType != null &&
    (FLEXIBLE_WORKSITE_TYPES as readonly string[]).includes(input.worksiteType);
  return flexible ? 'DAY' : 'CLOCK';
}

/** Was work expected? Reads only the calendar half of `DayStatus`. */
export function dueKindOf(status: string): DueKind {
  if (status === 'WEEKEND') return 'REST_DAY';
  if (status === 'HOLIDAY') return 'HOLIDAY';
  if (status === 'ON_LEAVE') return 'LEAVE';
  return 'WORKING_DAY';
}

/** What the caller knows that the row cannot say for itself. */
export interface AttendanceOpts {
  /**
   * Is this day still running? Only true for TODAY, in the company's own
   * timezone — a day is not "in progress" because a clock somewhere has not
   * reached midnight.
   */
  dayInProgress?: boolean;
}

/** The three columns that decide whether a day was ever closed. */
export interface AttendanceFacts {
  status: string;
  /** Explicit `null` is the midnight-close signature; `undefined` is unread. */
  lastPunchOut?: Date | string | null;
  totalWorkedMinutes?: number | null;
  firstPunchIn?: Date | string | null;
}

/**
 * Did they turn up?
 *
 * A REST DAY OR A HOLIDAY THAT WAS WORKED IS `ATTENDED`, and that is the whole
 * reason this is a separate field: today a worked Friday is stored `WEEKEND`
 * and the fact that somebody was there is lost behind a calendar label. Here
 * the calendar answer lives in `due` and the turning-up answer lives here, so
 * both survive.
 *
 * `undefined` is NOT `null` for `lastPunchOut`: `null` is a column that was
 * read and came back empty, `undefined` is a column nobody selected, and
 * unknown must not become unpaid. Same rule as `utils/payableDay.ts`, which is
 * where this predicate was first written down.
 */
export function attendanceOf(
  day: AttendanceFacts,
  opts?: AttendanceOpts
): AttendanceMark {
  /* A DAY STILL BEING WORKED IS NOT AN UNRESOLVED ONE.
     
     `UNRESOLVED` means a day that ENDED without a check-out — minutes the
     rollup manufactured by force-closing at local midnight. A worker who is on
     shift right now and has not punched out yet is simply at work, and marking
     them unresolved would depress today's attendance rate every morning and
     recover it every evening. The rollup already draws this line for itself:
     on an in-progress day it closes at `now`, which is a measurement, and only
     on a past day at midnight, which is not (see `utils/payableDay.ts`).
     
     The caller decides, because only the caller knows which day it is looking
     at. `dashboard.service`'s trend reads the SAME helper for today and for
     the past fortnight, and before this flag existed it had to mean one thing
     for both. */
  const unresolved = dayIsUnclosed(day);
  if (unresolved) return opts?.dayInProgress ? 'ATTENDED' : 'UNRESOLVED';
  if (day.status === 'ABSENT') return 'ABSENT';
  if (day.status === 'ON_LEAVE') return 'ABSENT';
  // A punch is proof of presence whatever the calendar says about the day.
  if (day.firstPunchIn != null) return 'ATTENDED';
  if (day.status === 'WEEKEND' || day.status === 'HOLIDAY') return 'ABSENT';
  return 'ATTENDED';
}

/** Everything the quality judgement reads. */
export interface QualityFacts {
  lateMinutes?: number | null;
  earlyLeaveMinutes?: number | null;
  overtimeMinutes?: number | null;
  /** Net worked minutes — what the target is measured against. */
  totalWorkedMinutes?: number | null;
}

/**
 * The headline judgement, or `null` when the basis makes none.
 *
 * ORDER MATTERS UNDER `CLOCK`, because a day can be both late and short. Late
 * wins: it is the one the office rule is written about, it is the one a
 * supervisor acts on, and `earlyLeaveMinutes` travels beside it regardless, so
 * nothing is hidden by the choice.
 */
export function qualityOf(
  facts: QualityFacts,
  basis: TimekeepingBasis,
  /** Net minutes owed. Only read under `HOURS`. */
  targetMinutes: number
): DayQuality | null {
  if (basis === 'DAY') return null;
  if (basis === 'CLOCK') {
    if ((facts.lateMinutes ?? 0) > 0) return 'LATE';
    if ((facts.earlyLeaveMinutes ?? 0) > 0) return 'LEFT_EARLY';
    return 'ON_TIME';
  }
  if ((facts.overtimeMinutes ?? 0) > 0) return 'OVER';
  // A target of zero is not a target. Without one there is nothing to be short
  // of, so the day is full rather than silently failing (rule 3.3).
  if (targetMinutes <= 0) return 'FULL_DAY';
  return (facts.totalWorkedMinutes ?? 0) < targetMinutes ? 'SHORT' : 'FULL_DAY';
}

/** All three answers for one day. */
export function labelDay(
  day: AttendanceFacts & QualityFacts,
  basis: TimekeepingBasis,
  targetMinutes: number,
  opts?: AttendanceOpts
): DayLabels {
  const attendance = attendanceOf(day, opts);
  return {
    due: dueKindOf(day.status),
    attendance,
    basis,
    // An absent or unresolved day has no quality: there is no day to judge.
    quality: attendance === 'ATTENDED' ? qualityOf(day, basis, targetMinutes) : null,
    lateMinutes: day.lateMinutes ?? 0,
    earlyLeaveMinutes: day.earlyLeaveMinutes ?? 0,
  };
}

/**
 * THE ONE PRESENT TEST. Every count, rate and tile reads this and nothing else,
 * which is the entire point of the file.
 */
export function isPresent(day: AttendanceFacts, opts?: AttendanceOpts): boolean {
  return attendanceOf(day, opts) === 'ATTENDED';
}

/**
 * WERE THEY HERE? — which is not the same question as `isPresent`.
 *
 * A day punched into and never out of cannot be PRICED, so `attendanceOf` calls
 * it UNRESOLVED and payroll refuses it. But the person was demonstrably at the
 * gate, and a site board asked "who was on this site on Tuesday" must say so:
 * it is read to settle disputes, and answering "nobody" about a worker whose
 * own punch is in the table is the worst thing it can do.
 *
 * So the site tallies read this and the money artefacts read `isPayable`. The
 * two differ on exactly one case, deliberately, and each says which it means.
 */
export function wasOnSite(day: AttendanceFacts): boolean {
  const mark = attendanceOf(day);
  return mark === 'ATTENDED' || mark === 'UNRESOLVED';
}

/** Payroll's question: may this day be priced at all? */
export function isPayable(day: AttendanceFacts): boolean {
  return attendanceOf(day) !== 'UNRESOLVED';
}
